import { useDisclosure, useToast } from '@chakra-ui/react'
import type React from 'react'
import InfoBox from '../../../../../../components/InfoBox'
import PersonalDetailItem from '../../../../../../components/PersonalDetailItem'
import Toast from '../../../../../../components/Toast'
import { useDeleteAffiliatePartnerMutation } from '../../../../../../redux/services/coreApi'
import type { AffiliatePartnerDto } from '../../../../../../types/coreApi-types'
import UpdateAffiliatePartnerModal from './UpdateAffiliatePartnerModal'

interface AffiliatePartnerDetailsProps {
  affiliatePartner?: AffiliatePartnerDto
  isLoading: boolean
}

const AffiliatePartnerDetails: React.FC<AffiliatePartnerDetailsProps> = ({
  affiliatePartner,
  isLoading,
}) => {
  const toast = useToast()
  const updateModal = useDisclosure()
  const [deleteAffiliatePartner, { isLoading: deleteLoading }] =
    useDeleteAffiliatePartnerMutation()

  const handleDelete = async () => {
    if (!affiliatePartner?.id) return

    const confirmed = window.confirm(
      `Are you sure you want to delete ${affiliatePartner.name}? This action cannot be undone.`
    )

    if (confirmed) {
      try {
        await deleteAffiliatePartner(affiliatePartner.id).unwrap()
        toast({
          position: 'top-right',
          render: () => (
            <Toast type="success">
              Affiliate partner deleted successfully!
            </Toast>
          ),
        })
        // Navigate back to the list
        window.history.back()
      } catch (error) {
        toast({
          position: 'top-right',
          render: () => (
            <Toast type="error">
              Failed to delete affiliate partner. Please try again.
            </Toast>
          ),
        })
      }
    }
  }

  if (isLoading) {
    return <div>Loading...</div>
  }

  if (!affiliatePartner) {
    return <div>Affiliate partner not found</div>
  }

  return (
    <div className="space-y-6">
      {/* Status and Actions */}
      <div className="card p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-xl font-semibold mb-2">Partner Status</h2>
            <InfoBox colorScheme={affiliatePartner.isActive ? 'green' : 'red'}>
              {affiliatePartner.isActive ? 'Active' : 'Inactive'}
            </InfoBox>
          </div>
          <div className="flex gap-3">
            <button className="button-secondary" onClick={updateModal.onOpen}>
              Edit Partner
            </button>
            <button
              className="button bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDelete}
              disabled={deleteLoading}
            >
              {deleteLoading ? 'Deleting...' : 'Delete Partner'}
            </button>
          </div>
        </div>
      </div>

      {/* Basic Information */}
      <div className="card p-6">
        <h2 className="text-xl font-semibold mb-4">Basic Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <PersonalDetailItem
            title="Partner Name"
            value={affiliatePartner.name}
          />
          <PersonalDetailItem
            title="Contact Email"
            value={affiliatePartner.contactEmail}
          />
          <PersonalDetailItem
            title="Commission Rate"
            value={`${(affiliatePartner.commissionRate * 100).toFixed(2)}%`}
          />
        </div>
      </div>

      {/* Partnership Information */}
      <div className="card p-6">
        <h2 className="text-xl font-semibold mb-4">Partnership Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <PersonalDetailItem
            title="Partnership Start Date"
            value={new Date(
              affiliatePartner.partnershipStartDate
            ).toLocaleDateString()}
          />
          <PersonalDetailItem
            title="Date Created"
            value={new Date(affiliatePartner.createdAt).toLocaleDateString()}
          />
          <div className="md:col-span-2">
            <PersonalDetailItem
              title="API Key"
              value={affiliatePartner.apiKey || 'Not generated'}
            />
          </div>
        </div>
      </div>

      {/* Public Affiliate Link */}
      {affiliatePartner.apiKey && (
        <div className="card p-6">
          <h2 className="text-xl font-semibold mb-4">Public Affiliate Link</h2>
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Share this link with your affiliate partner so they can view their
              public statistics dashboard.
            </p>
            <div className="flex-1">
              <code className="block bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded text-sm break-all">
                {`${window.location.origin}/affiliate/stats?apiKey=${affiliatePartner.apiKey}`}
              </code>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              <p>This link allows the affiliate partner to:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>View their performance statistics</li>
                <li>Track signups and completed KYC</li>
                <li>Monitor transaction volume and commission earned</li>
                <li>Filter data by date range</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Referral Codes */}
      <div className="card p-6">
        <h2 className="text-xl font-semibold mb-4">
          Referral Codes ({affiliatePartner.referralCodes?.length || 0})
        </h2>
        {affiliatePartner.referralCodes &&
        affiliatePartner.referralCodes.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {affiliatePartner.referralCodes.map((code, index) => (
              <div
                key={index}
                className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg"
              >
                <code className="font-mono text-sm">{code}</code>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No referral codes assigned</p>
        )}
      </div>

      <UpdateAffiliatePartnerModal
        isOpen={updateModal.isOpen}
        onClose={updateModal.onClose}
        affiliatePartner={affiliatePartner}
      />
    </div>
  )
}

export default AffiliatePartnerDetails
